import { DEFAULT_PAGE_SIZE } from '@/constants';
import type { ApiListResponse } from '@/types/api';
import {
  mapZeroBasedApiPageToUiPage,
  normalizeUiPageFromParams,
  type PaginationParams,
} from '@/utils/pagination';

type ApiListLike<TItem> = TItem[] | ApiListResponse<TItem>;

export type MappedListResult<TItem> = {
  data: TItem[];
  total: number;
  page: number;
  page_size: number;
  size?: number;
  total_pages?: number;
  last?: boolean;
};

type MapApiListResponseOptions<TSource, TMapped> = {
  defaultPageSize?: number;
  mapItem?: (item: TSource, index: number) => TMapped;
};

export const mapApiListResponse = <TSource, TMapped = TSource>(
  response: ApiListLike<TSource>,
  params: PaginationParams & { size?: number },
  options: MapApiListResponseOptions<TSource, TMapped> = {},
): MappedListResult<TMapped> => {
  const { defaultPageSize = DEFAULT_PAGE_SIZE, mapItem } = options;
  const mapper = mapItem ?? ((item: TSource) => item as unknown as TMapped);

  if (Array.isArray(response)) {
    const mapped = response.map((item, idx) => mapper(item, idx));
    const fallbackSize = (params.size ?? mapped.length) || defaultPageSize;
    const fallbackPage = normalizeUiPageFromParams(params);

    return {
      data: mapped,
      total: mapped.length,
      page: Math.max(fallbackPage, 1),
      page_size: fallbackSize,
      size: fallbackSize,
    };
  }

  const list = Array.isArray(response.data) ? response.data : [];
  const mapped = list.map((item, idx) => mapper(item, idx));
  const requestedPage = normalizeUiPageFromParams(params);
  const normalizedPage = mapZeroBasedApiPageToUiPage(
    response.page,
    requestedPage,
  );
  const pageSize =
    (response.size ?? params.size ?? mapped.length) || defaultPageSize;

  return {
    data: mapped,
    total: response.total ?? mapped.length,
    page: normalizedPage,
    page_size: pageSize,
    total_pages: response.total_pages,
    last: response.last,
  };
};
